import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, Music2, Filter, X, Disc3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import { API_URL } from '../utils/api';

export default function DJsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [djs, setDJs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchDJs();
  }, [searchParams]);

  const fetchDJs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const search = searchParams.get('search');
      if (search) params.append('search', search);
      if (selectedLocation) params.append('location', selectedLocation);
      if (selectedGenre) params.append('genre', selectedGenre);

      const response = await fetch(`${API_URL}/api/djs?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setDJs(data);
      }
    } catch (error) {
      console.error('Error fetching DJs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const applyFilters = () => {
    fetchDJs();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSelectedLocation('');
    setSelectedGenre('');
    setSearchQuery('');
    setSearchParams({});
    fetchDJs();
  };

  const ukRegions = ['Bedfordshire', 'Berkshire', 'Bristol', 'Buckinghamshire', 'Cambridgeshire', 'Cheshire', 'Cornwall', 'Cumbria', 'Derbyshire', 'Devon', 'Dorset', 'Durham', 'East Sussex', 'Essex', 'Gloucestershire', 'Greater London', 'Greater Manchester', 'Hampshire', 'Herefordshire', 'Hertfordshire', 'Isle of Wight', 'Kent', 'Lancashire', 'Leicestershire', 'Lincolnshire', 'Merseyside', 'Norfolk', 'North Yorkshire', 'Northamptonshire', 'Northumberland', 'Nottinghamshire', 'Oxfordshire', 'Rutland', 'Shropshire', 'Somerset', 'South Yorkshire', 'Staffordshire', 'Suffolk', 'Surrey', 'Tyne and Wear', 'Warwickshire', 'West Midlands', 'West Sussex', 'West Yorkshire', 'Wiltshire', 'Worcestershire', 'Scotland', 'Wales', 'Northern Ireland'];
  const genres = ['Country', 'Western Swing', 'Honky Tonk', 'Traditional Country', 'Country Pop', 'Line Dance Classics', 'Two-Step', 'Mixed/Top 40'];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="djs-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-700 to-purple-600 text-white py-12">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="bg-white/20 text-white mb-4 px-4 py-1.5">
              <Disc3 className="w-4 h-4 mr-2" />
              Spin the Tunes
            </Badge>
            <h1 className="text-3xl lg:text-4xl font-extrabold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Country DJs
            </h1>
            <p className="text-violet-100 max-w-lg mx-auto">
              Find the perfect DJ to keep your boots movin' all night long
            </p>
          </motion.div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 lg:px-12 py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search DJs by name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-11 rounded-lg border-slate-200"
                  data-testid="djs-search-input"
                />
              </div>
              <Button type="submit" className="h-11 bg-violet-600 hover:bg-violet-500" data-testid="djs-search-btn">
                Search
              </Button>
            </form>
            
            <Button
              variant="outline"
              className="h-11 border-slate-200"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="djs-filter-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 pt-4 border-t border-slate-200"
            >
              <div className="flex flex-wrap gap-4 items-end">
                <div className="w-48">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Location</label>
                  <Select value={selectedLocation} onValueChange={(val) => setSelectedLocation(val === "all" ? "" : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {ukRegions.map(region => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-48">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Genre</label>
                  <Select value={selectedGenre} onValueChange={(val) => setSelectedGenre(val === "all" ? "" : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Genres" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Genres</SelectItem>
                      {genres.map(genre => (
                        <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button onClick={applyFilters} className="bg-violet-600 hover:bg-violet-500">
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* DJs Grid */}
      <div className="container mx-auto px-6 lg:px-12 py-8">
        <p className="text-slate-600 mb-6">{djs.length} DJ{djs.length !== 1 ? 's' : ''} found</p>
        
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="h-48 bg-slate-200 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-6 bg-slate-200 rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : djs.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl">
            <Disc3 className="w-20 h-20 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-700 mb-2">No DJs found</h2>
            <p className="text-slate-400 mb-6">Try adjusting your search or be the first to list!</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={clearFilters} variant="outline">
                Clear Search
              </Button>
              <Link to="/dashboard/add-dj">
                <Button className="bg-violet-600 hover:bg-violet-500">
                  List Yourself as DJ
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {djs.map((dj, index) => (
              <motion.div
                key={dj.dj_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/djs/${dj.dj_id}`} data-testid={`dj-item-${dj.dj_id}`}>
                  <div className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-48 overflow-hidden bg-gradient-to-br from-violet-600 to-purple-700">
                      {dj.image_url ? (
                        <img
                          src={dj.image_url}
                          alt={dj.stage_name || dj.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <img
                          src="https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/4fad027c0b6ceb7cac8dae85ef2ee610c7e3d96a2cc6f2d032a22e2350b0313e.png"
                          alt="Country DJ"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 opacity-60"
                        />
                      )}
                      {dj.is_featured && (
                        <div className="absolute top-3 left-3">
                          <Badge className="bg-violet-500 text-white hover:bg-violet-500">Featured DJ</Badge>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-violet-600 transition-colors">
                        {dj.stage_name || dj.name}
                      </h3>
                      {dj.stage_name && (
                        <p className="text-sm text-slate-400">{dj.name}</p>
                      )}
                      <p className="flex items-center text-slate-400 text-sm mt-2 mb-3">
                        <MapPin className="w-4 h-4 mr-1" />
                        {dj.location || [dj.location_city, dj.location_state].filter(Boolean).join(', ') || 'Location not specified'}
                      </p>
                      <p className="text-slate-600 text-sm line-clamp-2 mb-4">{dj.bio}</p>
                      <div className="flex flex-wrap gap-2">
                        {dj.genres?.slice(0, 3).map((genre, i) => (
                          <Badge 
                            key={i} 
                            variant="secondary"
                            className="bg-violet-100 text-violet-800 text-xs"
                          >
                            {genre}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
